import React, { useCallback } from 'react';
import './GeoPlaybook.scss';
import { useTranslation } from 'react-i18next';
import { ChecklistTab } from '../../data/checklistData';
import PlaybookTakeaways from './PlaybookTakeaways';
import PlaybookSection from './PlaybookSection';
import impactChart from '../../../../assets/images/geo-impact-chart.png';

const SCROLL_OFFSET = 80;

interface GeoPlaybookProps {
  tab: ChecklistTab;
  checkedTasks: Record<string, string>;
  onToggle: (taskId: string) => void;
  progress: { checked: number; total: number; percent: number };
}

const GeoPlaybook: React.FC<GeoPlaybookProps> = ({ tab, checkedTasks, onToggle, progress }) => {
  const { t } = useTranslation();

  const scrollToSection = useCallback((sectionId?: string) => {
    if (!sectionId) return;
    const el = document.getElementById(`section-${sectionId}`);
    if (!el) return;
    const scrollContainer = el.closest('.layout__right');
    if (scrollContainer) {
      const top =
        el.getBoundingClientRect().top -
        scrollContainer.getBoundingClientRect().top +
        scrollContainer.scrollTop -
        SCROLL_OFFSET;
      scrollContainer.scrollTo({ top, behavior: 'smooth' });
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const isComplete = progress.total > 0 && progress.checked === progress.total;

  const sectionRows: ChecklistTab['sections'][] = [];
  for (let i = 0; i < tab.sections.length; i += 2) {
    sectionRows.push(tab.sections.slice(i, i + 2));
  }

  return (
    <div className="playbook">
      <div className="stats stats--two-cols">
        <div className="stats__panel">
          <div className="stats-box stats-box--without-margin">
            <div className="stats-box__content playbook-stat">
              <div className="playbook-stat__main">
                <div className="playbook-stat__heading">{t('geoChecklist.pb.actionPlanProgress')}</div>
                <div className="playbook-stat__count">
                  <span className="playbook-stat__count-current">{progress.checked}</span>{' '}
                  {t('geoChecklist.pb.of')}{' '}
                  <span className="playbook-stat__count-total">{progress.total}</span>{' '}
                  <span className="playbook-stat__count-label">
                    {t('geoChecklist.pb.tasksCompleted')}
                  </span>
                </div>
                <div className="playbook-stat__progress-row">
                  <span className="playbook-stat__bar">
                    <span
                      className={`playbook-stat__bar-fill${isComplete ? ' playbook-stat__bar-fill--complete' : ''}`}
                      style={{ width: `${progress.percent}%` }}
                    />
                  </span>
                  <span
                    className={`playbook-stat__percent${isComplete ? ' playbook-stat__percent--complete' : ''}`}
                  >
                    {progress.percent}%
                  </span>
                </div>
                <p className="playbook-stat__hint">{t('geoChecklist.pb.actionPlanHint')}</p>
              </div>

              {isComplete && (
                <div className="playbook-stat__celebrate" aria-hidden="true">
                  <span className="playbook-stat__celebrate-spark playbook-stat__celebrate-spark--1" />
                  <span className="playbook-stat__celebrate-spark playbook-stat__celebrate-spark--2" />
                  <span className="playbook-stat__celebrate-spark playbook-stat__celebrate-spark--3" />
                  <span className="playbook-stat__celebrate-spark playbook-stat__celebrate-spark--4" />
                  <div className="playbook-stat__celebrate-circle">
                    <span className="ico--tick ico--md" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="stats__panel">
          <div className="stats-box stats-box--without-margin playbook-impact-box">
            <div className="stats-box__content playbook-impact">
              <div className="playbook-impact__main">
                <div className="playbook-stat__heading">{t('geoChecklist.pb.aiAnswersTitle')}</div>
                <p className="playbook-impact__body">{t('geoChecklist.pb.aiAnswersBody')}</p>
                <a
                  className="btn btn--sm btn--black playbook-impact__cta"
                  href="https://chatbeat.com/ai-seo-rank-in-chatgpt"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('improveAIRanking')}
                </a>
              </div>
              <div className="playbook-impact__chart-col">
                <img className="playbook-impact__chart" src={impactChart} alt="" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {tab.introKeyTakeaways && tab.introKeyTakeaways.length > 0 && (
        <div className="stats stats--full-width">
          <PlaybookTakeaways
            takeaways={tab.introKeyTakeaways}
            title={t(tab.introHelperKey)}
            onTakeawayClick={scrollToSection}
          />
        </div>
      )}

      <div className="playbook__sections">
        {sectionRows.map((row) => (
          <div key={row[0].id} className="stats stats--two-cols">
            {row.map((section) => (
              <PlaybookSection
                key={section.id}
                section={section}
                checkedTasks={checkedTasks}
                onToggle={onToggle}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export { GeoPlaybook };
export default GeoPlaybook;
